import type { ApolloCache } from '@apollo/client';
import {
  useApolloClient,
  useMutation as useApolloMutation,
} from '@apollo/client';
import {
  useMutation as useTanstackMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useContext, useRef } from 'react';
import type { ChangeRequestConfirmContextValue } from 'src/providers/change-request-confirm-context';
import { ChangeRequestConfirmContext } from 'src/providers/change-request-confirm-context';
import { useTRPC } from 'src/utils/trpc';

import { withChangeRequestConfirm } from '@/hooks/mutations/_with-change-request-confirm';
import { invalidateChangeRequestFeeds } from '@/hooks/mutations/invalidateChangeRequestFeeds';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type {
  MutationHookConfig,
  MutationHookFactoryResult,
  MutationHookResult,
  MutationRequestContext,
} from './types';

/**
 * Factory that creates a GraphQL-to-tRPC mutation hook — the mutation
 * counterpart of `createQueryHook`. It collapses the previous two-file pattern
 * (`useXxx.tsx` wrapper + `useXxxTRPC.tsx`) into a single declarative config.
 *
 * The returned hook:
 * - switches between the Apollo mutation and the tRPC mutation on the `trpc` flag,
 * - preserves the per-entity method name (so consumers are untouched),
 * - keeps cache-invalidation parity (Apollo cache eviction ↔ tanstack `invalidateQueries`).
 *
 * Change-request behaviour is opt-in (`isChangeRequestGated` /
 * `refreshChangeRequestFeedsWhen`). The factory picks a **governed** or a
 * **plain** hook body at creation time, so a plain mutation never reads the
 * change-request confirm context at render — only governed hooks depend on it.
 *
 * It deliberately does NOT add UI side-effects (toasts) or pass
 * `throwOnError` — `FormContext` owns mutation UX (see packages/web/CLAUDE.md).
 */
export const createMutationHook = <
  TInput,
  TTrpcInput,
  TTrpcResult,
  TGraphQLOutput,
  TGraphQLData,
  TName extends string,
  TOptions = void,
>(
  config: MutationHookConfig<
    TInput,
    TTrpcInput,
    TTrpcResult,
    TGraphQLOutput,
    TGraphQLData,
    TName,
    TOptions
  >
): MutationHookFactoryResult<TName, TInput, TGraphQLOutput, TOptions> => {
  const {
    name,
    isIdempotentCreate,
    idempotencyScope,
    mutationOptions,
    mapInput,
    mapResponse,
    invalidate,
    invalidateOnGraphql,
    invalidatesNavCounts = true,
    isChangeRequestGated,
    refreshChangeRequestFeedsWhen,
    graphqlDocument,
    graphqlVariables,
    validateGraphqlResponse,
    mapGraphqlResponse,
    evictFields,
    refetchQueries,
    awaitRefetchQueries,
    apolloContext,
  } = config;

  // Per-render setup shared by the governed and plain hook bodies.
  const useMutationCore = () => {
    const trpcEnabled = useIsFeatureFlagEnabled('trpc');
    const trpc = useTRPC();
    const queryClient = useQueryClient();

    const apolloClient = useApolloClient();
    const idempotencyKeysRef = useRef<Map<string, string> | undefined>(
      undefined
    );
    if (!idempotencyKeysRef.current) {
      idempotencyKeysRef.current = new Map();
    }
    type IdempotentMutationCall = {
      input: TTrpcInput;
      requestContext: MutationRequestContext;
    };

    const getIdempotencyScope = (input: TInput): string =>
      idempotencyScope?.(input) ?? 'mounted-hook';

    const getRequestContext = (input: TInput): MutationRequestContext => {
      if (!isIdempotentCreate) {
        return {};
      }

      const scope = getIdempotencyScope(input);
      const existingKey = idempotencyKeysRef.current?.get(scope);
      if (existingKey) {
        return { idempotencyKey: existingKey };
      }

      const idempotencyKey = crypto.randomUUID();
      idempotencyKeysRef.current?.set(scope, idempotencyKey);

      return { idempotencyKey };
    };

    const clearIdempotencyKeyIfCurrent = (
      input: TInput,
      requestContext: MutationRequestContext
    ): void => {
      const completedKey = requestContext.idempotencyKey;
      if (!completedKey) {
        return;
      }

      const scope = getIdempotencyScope(input);
      if (idempotencyKeysRef.current?.get(scope) === completedKey) {
        idempotencyKeysRef.current.delete(scope);
      }
    };

    // Evict every field, then a single gc() pass — cheaper than the per-field
    // gc() the shared evictField helper does (RSP-6326 review).
    const evictApolloFields = (cache: ApolloCache<unknown>) => {
      if (!evictFields) {
        return;
      }
      evictFields.forEach((fieldName) =>
        cache.evict({ id: 'ROOT_QUERY', fieldName })
      );
      cache.gc();
    };

    // GraphQL mutation (flag off)
    const [graphqlMutation, graphqlState] = useApolloMutation(graphqlDocument, {
      update: evictFields ? evictApolloFields : undefined,
      refetchQueries,
      awaitRefetchQueries,
    });

    // tRPC mutation (flag on)
    const onSuccess = async (result: TTrpcResult): Promise<void> => {
      if (refreshChangeRequestFeedsWhen?.(result)) {
        await invalidateChangeRequestFeeds(queryClient, trpc);
      }
      for (const queryKey of invalidate?.(trpc) ?? []) {
        await queryClient.invalidateQueries({ queryKey });
      }
      // Left-nav badge counts (COI-908), on by default — see
      // `invalidatesNavCounts` in QueryHookConfig for when to opt out.
      //
      // Before the collapse, each badge had its own query family and every
      // mutation that moved a number had to name the matching `*Count` key in
      // `invalidate` — 58 such entries across 45 hooks, and forgetting one left
      // a badge stale until reload (COI-889). All badges now read ONE procedure,
      // so invalidating that single key here refreshes every badge and deletes
      // that whole class of omission.
      //
      // Be precise about the cost, because it is easy to under-count: the nav is
      // mounted for the whole session so this query is always ACTIVE, and
      // `invalidateQueries` defaults to `refetchType: 'active'` — it refetches
      // immediately and `staleTime` does NOT suppress it. So this is one request
      // running 26 counts per successful mutation, including for mutations that
      // cannot move a badge. That is why the opt-out exists.
      if (invalidatesNavCounts) {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.navigation.counts.queryKey(),
        });
      }
      // `evictFields` applies on the tRPC path too, not just the Apollo one
      // (ENG-616). `invalidate` only reaches tanstack, so while a read is still
      // UNMIGRATED and cache-first on Apollo, a write via tRPC would otherwise
      // leave its list or counter showing pre-write data — that is the stale
      // oversight `(0)` count. Evicting a field the migrated reads no longer use
      // is a no-op, so this is safe to apply unconditionally and can simply be
      // deleted from a config once that entity's reads have migrated.
      evictApolloFields(apolloClient.cache);
    };
    const optionsWithFactoryOwnedLifecycle = mutationOptions(
      trpc,
      onSuccess,
      {}
    ) as ReturnType<typeof mutationOptions> & {
      onSuccess?: unknown;
      onMutate?: unknown;
      onError?: unknown;
      onSettled?: unknown;
    };
    const {
      mutationFn: baseMutationFn,
      onSuccess: _baseOnSuccess,
      onMutate: _baseOnMutate,
      onError: _baseOnError,
      onSettled: _baseOnSettled,
      ...baseMutationOptions
    } = optionsWithFactoryOwnedLifecycle;
    const mutationFn = async (
      input: TTrpcInput | IdempotentMutationCall,
      mutationFunctionContext: Parameters<NonNullable<typeof baseMutationFn>>[1]
    ): Promise<TTrpcResult> => {
      if (!isIdempotentCreate) {
        if (!baseMutationFn) {
          throw new Error(`${name}: tRPC mutation options require mutationFn`);
        }

        return await baseMutationFn(
          input as TTrpcInput,
          mutationFunctionContext
        );
      }

      const call = input as IdempotentMutationCall;
      const scopedMutationFn = mutationOptions(
        trpc,
        onSuccess,
        call.requestContext
      ).mutationFn;
      if (!scopedMutationFn) {
        throw new Error(`${name}: tRPC mutation options require mutationFn`);
      }

      return await scopedMutationFn(call.input, mutationFunctionContext);
    };
    const trpcMutation = useTanstackMutation({
      ...baseMutationOptions,
      mutationFn: isIdempotentCreate ? mutationFn : baseMutationFn,
      onSuccess,
    });

    const runTrpcMutation = async (
      input: TTrpcInput,
      requestContext: MutationRequestContext
    ): Promise<TTrpcResult> => {
      if (!isIdempotentCreate) {
        return await trpcMutation.mutateAsync(input);
      }

      // TanStack infers TTrpcInput from the non-idempotent branch above, but
      // this factory configuration is immutable and the idempotent branch's
      // mutationFn consumes the internal envelope before calling tRPC.
      const mutateIdempotent = trpcMutation.mutateAsync as unknown as (
        variables: IdempotentMutationCall
      ) => Promise<TTrpcResult>;

      return await mutateIdempotent({
        input,
        requestContext,
      });
    };

    return {
      trpcEnabled,
      graphqlMutation,
      graphqlState,
      trpcMutation,
      runTrpcMutation,
      trpc,
      queryClient,
      getRequestContext,
      clearIdempotencyKeyIfCurrent,
    };
  };

  // Assembles the public `{ [name], loading, error }` from the per-render core
  // plus the resolved change-request confirm context (null for plain hooks).
  // Not a hook — pure assembly — so both variants share the run() logic.
  const buildResult = (
    core: ReturnType<typeof useMutationCore>,
    changeRequestConfirm: ChangeRequestConfirmContextValue | null
  ): MutationHookResult<TName, TInput, TGraphQLOutput, TOptions> => {
    const {
      trpcEnabled,
      graphqlMutation,
      graphqlState,
      trpcMutation,
      runTrpcMutation,
      trpc,
      queryClient,
      getRequestContext,
      clearIdempotencyKeyIfCurrent,
    } = core;

    const run = async (
      input: TInput,
      options?: TOptions
    ): Promise<TGraphQLOutput> => {
      if (trpcEnabled) {
        const requestContext = getRequestContext(input);
        const trpcInput = mapInput(input);

        let result: TTrpcResult;
        if (isChangeRequestGated) {
          if (!changeRequestConfirm) {
            throw new Error(
              `${name}: change-request-gated mutation requires ChangeRequestConfirmProvider`
            );
          }
          result = await withChangeRequestConfirm({
            input: trpcInput,
            run: (variables) => runTrpcMutation(variables, requestContext),
            confirm: changeRequestConfirm,
          });
        } else {
          result = await runTrpcMutation(trpcInput, requestContext);
        }

        const output = mapResponse(result, input);
        clearIdempotencyKeyIfCurrent(input, requestContext);

        return output;
      }

      const graphqlResult = await graphqlMutation({
        variables: graphqlVariables(input, options),
        context: apolloContext?.(options),
      });
      if (!graphqlResult.data) {
        throw new Error(`Failed to run ${name}`);
      }
      validateGraphqlResponse?.(graphqlResult.data);
      // Invalidate only after the response is confirmed present and valid — a
      // no-data result or a throwing `validateGraphqlResponse` means the
      // mutation failed, so its read caches must not be invalidated.
      for (const queryKey of invalidateOnGraphql?.(trpc) ?? []) {
        await queryClient.invalidateQueries({ queryKey });
      }

      // Without a mapper the document shape IS the consumer shape (the common
      // case); the cast is the type-level assertion of that invariant. With a
      // mapper the two branches converge onto TGraphQLOutput explicitly.
      return mapGraphqlResponse
        ? mapGraphqlResponse(graphqlResult.data, input)
        : (graphqlResult.data as unknown as TGraphQLOutput);
    };

    // The mapped return type names the mutate fn per-entity (e.g. `updateAction`);
    // a computed-key object literal can't be inferred as that mapped type.
    return {
      [name]: run,
      loading: trpcEnabled ? trpcMutation.isPending : graphqlState.loading,
      error: trpcEnabled ? trpcMutation.error : (graphqlState.error ?? null),
    } as MutationHookResult<TName, TInput, TGraphQLOutput, TOptions>;
  };

  // Governed hooks read the change-request confirm context; plain hooks never
  // touch it. The choice is fixed at factory-creation time, so each returned
  // hook has a stable hook-call order.
  const useGovernedMutationHook = () =>
    buildResult(useMutationCore(), useContext(ChangeRequestConfirmContext));
  const usePlainMutationHook = () => buildResult(useMutationCore(), null);

  return isChangeRequestGated ? useGovernedMutationHook : usePlainMutationHook;
};
